import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { Toast as RadixToast } from 'radix-ui';
import type { ReactNode } from 'react';
import { create } from 'zustand';

import { cn } from '@/utils/cn';

/**
 * Transient feedback.
 *
 * A toast confirms something that already happened. It never asks a question, never
 * carries the only copy of information, and never reports an error the user must act
 * on — those are a dialog and an inline message respectively. A toast the user misses
 * must cost them nothing.
 */

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  /** Milliseconds before auto-dismiss; defaults to 5s. */
  durationMs?: number;
}

interface ToastState {
  items: ToastItem[];
  push: (item: Omit<ToastItem, 'id'>) => string;
  dismiss: (id: string) => void;
}

export const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (item) => {
    const id = crypto.randomUUID();
    set((state) => ({ items: [...state.items, { ...item, id }] }));
    return id;
  },
  dismiss: (id) =>
    set((state) => ({ items: state.items.filter((item) => item.id !== id) })),
}));

/** Callable from anywhere, including outside React — a mutation callback, an interceptor. */
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'success', title, description }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'error', title, description }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'warning', title, description }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ tone: 'info', title, description }),
  custom: (item: Omit<ToastItem, 'id'>) => useToastStore.getState().push(item),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};

const ICONS: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 className="h-4.5 w-4.5 text-success" />,
  error: <AlertCircle className="h-4.5 w-4.5 text-danger" />,
  warning: <TriangleAlert className="h-4.5 w-4.5 text-warning" />,
  info: <Info className="h-4.5 w-4.5 text-info" />,
};

export const Toaster = ({ closeLabel }: { closeLabel?: string }) => {
  const items = useToastStore((state) => state.items);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <RadixToast.Provider swipeDirection="right">
      {items.map((item) => (
        <RadixToast.Root
          key={item.id}
          duration={item.durationMs ?? 5000}
          onOpenChange={(open) => !open && dismiss(item.id)}
          // Errors are announced immediately; confirmations wait for a gap in speech
          // so they do not interrupt what the user is reading.
          type={item.tone === 'error' ? 'foreground' : 'background'}
          className={cn(
            'flex items-start gap-3 rounded-lg border border-border bg-surface-raised p-3.5 shadow-e3',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-4',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          )}
        >
          <span className="mt-0.5 shrink-0">{ICONS[item.tone]}</span>

          <div className="min-w-0 flex-1">
            <RadixToast.Title className="text-body font-medium text-fg">
              {item.title}
            </RadixToast.Title>
            {item.description ? (
              <RadixToast.Description className="mt-0.5 text-body-sm text-fg-secondary">
                {item.description}
              </RadixToast.Description>
            ) : null}
          </div>

          {item.action ? (
            <RadixToast.Action
              altText={item.action.label}
              onClick={item.action.onClick}
              className="shrink-0 rounded-md px-2 py-1 text-body-sm font-medium text-brand hover:bg-surface-sunken"
            >
              {item.action.label}
            </RadixToast.Action>
          ) : null}

          <RadixToast.Close
            aria-label={closeLabel}
            className="shrink-0 rounded p-0.5 text-fg-tertiary hover:text-fg"
          >
            <X className="h-4 w-4" />
          </RadixToast.Close>
        </RadixToast.Root>
      ))}

      <RadixToast.Viewport className="fixed right-4 bottom-4 z-[70] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 outline-none" />
    </RadixToast.Provider>
  );
};
